import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useSiteId } from "../hooks/useSiteId";
import { useWarehouseId } from "../hooks/useWarehouseId";
import { AutoGrid } from "../app/layout/AutoGrid";
import { getFleetReport } from "../api/rest";
import { fmtDistance, fmtHours, fmtMoney, fmtNum, fmtPct100 } from "../app/format";
import { whatIf, type ShiftDay } from "./fleetWhatIf";
import styles from "./FleetPage.module.css";
import tabStyles from "./AlertsPage.module.css";
import oeStyles from "./OeePage.module.css";

/**
 * ADR 288 (movement-cost slice 1): Análise ▸ Custo de Frota — the measured
 * counterpart of the GTP commercial model. Per forklift: hours by state,
 * distance (move sessions only — IDLE metres are SLAM wander), movimentações
 * (completed loaded moves), % viagem vazia, utilization vs the shift
 * calendar, and cost at the configured hourly rate. No rate = "—", never an
 * invented number, with a pointer to the rates admin.
 */

type Range = "today" | "7d" | "30d";

function rangeWindow(r: Range): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to);
  if (r === "today") from.setHours(0, 0, 0, 0);
  else if (r === "7d") from.setDate(from.getDate() - 7);
  else from.setDate(from.getDate() - 30);
  return { from, to };
}

function ValueTile({ label, value, hint, money }: { label: string; value: string; hint?: string; money?: boolean }) {
  return (
    <div className={oeStyles.tile}>
      <span className={oeStyles.tileLabel}>{label}</span>
      <span className={money ? `${oeStyles.tileValue} cost` : oeStyles.tileValue}>{value}</span>
      {hint && <span className={oeStyles.tileLabel}>{hint}</span>}
    </div>
  );
}

/** ADR 291: the fewer-forklifts what-if — per-shift-day capacity, slider
 *  recomputes client-side (fleetWhatIf.ts), limits stated inline. */
function WhatIfSection({ shiftDays, fleetSize, lang }: {
  shiftDays: ShiftDay[]; fleetSize: number; lang: string;
}) {
  const { t } = useTranslation();
  const [n, setN] = useState(fleetSize);
  const chosen = Math.min(Math.max(1, n), fleetSize);
  const r = whatIf(shiftDays, chosen);
  return (
    <section className={oeStyles.summary} data-testid="what-if" style={{ marginTop: 8 }}>
      <h2 style={{ fontSize: 15, margin: "0 0 6px" }}>{t("fleetWhatIf.title")}</h2>
      <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
        {t("fleetWhatIf.forklifts")}
        <input type="range" min={1} max={fleetSize} value={chosen}
               onChange={(e) => setN(Number(e.target.value))} />
        <span style={{ fontFamily: "var(--font-mono)" }}>{chosen} / {fleetSize}</span>
      </label>
      <AutoGrid min={190}>
        <ValueTile label={t("fleetWhatIf.utilization")} value={fmtPct100(r.utilizationPct, lang)} />
        <ValueTile label={t("fleetWhatIf.overflow")}
                   value={t("fleetWhatIf.overflowValue", { overflow: r.overflowDays, days: r.days })} />
        <ValueTile label={t("fleetWhatIf.overtime")} value={fmtHours(r.overtimeHours * 3_600_000, lang)} />
      </AutoGrid>
      <p style={{ fontSize: 12, color: "var(--op-ink-500)", marginTop: 6 }}>{t("fleetWhatIf.limits")}</p>
    </section>
  );
}

export function FleetReportPage() {
  const siteId = useSiteId();
  const warehouseId = useWarehouseId();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [range, setRange] = useState<Range>("today");
  const { from, to } = useMemo(() => rangeWindow(range), [range]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["fleet-report", siteId, warehouseId, range],
    queryFn: () => getFleetReport(siteId, warehouseId!, from.getTime(), to.getTime()),
    enabled: !!siteId && !!warehouseId,
    refetchInterval: range === "today" ? 60_000 : false,
    retry: 0,
  });

  const fleet = data?.fleet;
  const currency = data?.currency ?? "BRL";

  return (
    <div className={styles.page}>
      <h1 className={styles.h1}>{t("fleetReport.title")}</h1>

      <div className={tabStyles.tabs} role="tablist">
        {(["today", "7d", "30d"] as Range[]).map((r) => (
          <button
            key={r}
            role="tab"
            aria-selected={range === r}
            className={range === r ? tabStyles.tabActive : tabStyles.tab}
            onClick={() => setRange(r)}
          >
            {t(`oee.range.${r}`)}
          </button>
        ))}
      </div>

      {fleet && (
        <div className={oeStyles.summary}>
          <AutoGrid min={190}>
            <ValueTile money label={t("fleetReport.kpi.cost")} value={fmtMoney(fleet.cost, currency, lang)}
                       hint={fleet.unpricedAssets > 0 ? t("fleetReport.unpriced", { count: fleet.unpricedAssets }) : undefined} />
            <ValueTile money label={t("fleetReport.kpi.costPerMove")} value={fmtMoney(fleet.costPerMove, currency, lang)} />
            <ValueTile label={t("fleetReport.kpi.moves")} value={fleet.moves.toLocaleString(lang)} />
            <ValueTile label={t("fleetReport.kpi.emptyTime")} value={fmtPct100(fleet.emptyTimePct, lang)} />
            <ValueTile label={t("fleetReport.kpi.emptyDistance")} value={fmtPct100(fleet.emptyDistancePct, lang)} />
            <ValueTile label={t("fleetReport.kpi.distance")} value={fmtDistance(fleet.distanceM, lang)} />
          </AutoGrid>
          {fleet.unpricedAssets > 0 && (
            <p role="status" style={{ fontSize: 12, color: "var(--op-ink-500)", marginTop: 8 }}>
              {t("fleetReport.unpricedNote", { count: fleet.unpricedAssets })}{" "}
              <Link to="/admin/rates">{t("fleetReport.unpricedLink")}</Link>
            </p>
          )}
        </div>
      )}

      {data && fleet && fleet.assets > 0 && (data.shiftDays?.length ?? 0) > 0 && (
        <WhatIfSection shiftDays={data.shiftDays!} fleetSize={fleet.assets} lang={lang} />
      )}

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("fleetReport.col.asset")}</th>
              <th>{t("fleetReport.col.type")}</th>
              <th>{t("fleetReport.col.hours")}</th>
              <th>{t("fleetReport.col.loaded")}</th>
              <th>{t("fleetReport.col.empty")}</th>
              <th>{t("fleetReport.col.idle")}</th>
              <th>{t("fleetReport.col.emptyPct")}</th>
              <th>{t("fleetReport.col.distance")}</th>
              <th>{t("fleetReport.col.moves")}</th>
              <th>{t("fleetReport.col.speed")}</th>
              <th>{t("fleetReport.col.utilization")}</th>
              <th>{t("fleetReport.col.cost")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 3 }, (_, i) => (
                <tr key={i}>
                  {Array.from({ length: 12 }, (_, j) => (
                    <td key={j}><span className="skeleton" style={{ width: 48, height: 14, display: "block" }} /></td>
                  ))}
                </tr>
              ))
            ) : isError ? (
              <tr><td colSpan={12} className={styles.error}>{t("fleetReport.error")}</td></tr>
            ) : !data || data.assets.length === 0 ? (
              <tr><td colSpan={12} className={styles.empty}>{t("fleetReport.empty")}</td></tr>
            ) : (
              data.assets.map((a) => (
                <tr key={a.assetId}>
                  <td className={styles.mono}>
                    {a.label ?? a.assetId}
                    {!a.registered && <span title={t("fleetReport.unregistered")}> *</span>}
                  </td>
                  <td className={styles.mono}>{a.typeCode ?? "—"}</td>
                  <td className={styles.mono}>{fmtHours(a.runMs, lang)}</td>
                  <td className={styles.mono}>{fmtHours(a.loadedMs, lang)}</td>
                  <td className={styles.mono}>{fmtHours(a.emptyMs, lang)}</td>
                  <td className={styles.mono}>{fmtHours(a.idleMs, lang)}</td>
                  <td className={styles.mono}>{fmtPct100(a.emptyTimePct, lang)}</td>
                  <td className={styles.mono}>{fmtDistance(a.distanceM, lang)}</td>
                  <td className={styles.mono}>{a.moves.toLocaleString(lang)}</td>
                  <td className={styles.mono}>{a.avgSpeedMps == null ? "—" : `${fmtNum(a.avgSpeedMps, lang, 2)} m/s`}</td>
                  <td className={styles.mono}>{fmtPct100(a.utilizationPct, lang)}</td>
                  <td className={`${styles.mono} cost`}>{fmtMoney(a.cost, currency, lang)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 12, color: "var(--op-ink-500)", marginTop: 8 }}>{t("fleetReport.movesNote")}</p>
    </div>
  );
}
